import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { MessageCircle, X, BellRing, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function PendingWhatsAppCard() {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [insights, setInsights] = useState({}); // {template_id: {sends, replies_2h, rate}}
  const [busyId, setBusyId] = useState(null);
  const [nudgeTemplate, setNudgeTemplate] = useState({}); // {pendingId: templateId}
  // Session Z51 — hourly cleanup heartbeat so operators see the sweep is alive.
  const [cronHealth, setCronHealth] = useState(null);

  const load = () => {
    api.get("/inbox/whatsapp-pending")
      .then((r) => setRows(r.data?.pending || []))
      .catch(() => setRows([]));
    api.get("/inbox/whatsapp-templates")
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => {});
    // Session Z48 — per-template "% replied in 2h" so owners pick the voice
    // that actually gets a response.
    api.get("/inbox/whatsapp-templates/nudge-insights")
      .then((r) => {
        const map = {};
        (r.data?.insights || []).forEach((i) => { map[i.template_id] = i; });
        setInsights(map);
      })
      .catch(() => setInsights({}));
    api.get("/cron/health/pending-expiry")
      .then((r) => setCronHealth(r.data || null))
      .catch(() => setCronHealth(null));
  };

  useEffect(() => { load(); }, []);

  const nudge = async (p) => {
    setBusyId(p.id);
    try {
      const tplId = nudgeTemplate[p.id];
      await api.post(`/inbox/whatsapp-pending/${p.id}/nudge`, {
        template_id: tplId || null,
      });
      toast.success(`Nudge sent to ${p.phone}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err, "Nudge failed"));
    } finally { setBusyId(null); }
  };

  const clear = async (p) => {
    if (!window.confirm(`Clear the open WhatsApp menu for ${p.phone}?`)) return;
    setBusyId(p.id);
    try {
      await api.delete(`/inbox/whatsapp-pending/${p.id}`);
      toast.success("Pending menu cleared");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusyId(null); }
  };

  return (
    <div className="ns-card p-6" data-testid="settings-whatsapp-pending-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="ns-label">Open WhatsApp prompts</div>
          <div className="font-serif text-xl mt-1">Awaiting cleaner reply</div>
          <div className="text-xs text-slate-400 mt-1">
            Cleaners who got a "which unit?" menu but haven't picked yet. Expires after 24h.
          </div>
        </div>
        <div className="flex items-center gap-1">
          {rows.length > 0 && (
            <a
              href="/api/inbox/whatsapp-pending.csv"
              target="_blank"
              rel="noreferrer"
              data-testid="settings-whatsapp-pending-csv"
              className="text-[11px] rounded-full bg-slate-900 text-white px-2.5 py-1 hover:bg-slate-800 transition inline-flex items-center gap-1"
              title="Download open prompts as CSV"
            >
              ⬇ CSV
            </a>
          )}
          <button
            type="button"
            onClick={load}
            className="p-2 text-slate-400 hover:text-slate-800 rounded-lg hover:bg-stone-50"
            title="Refresh"
            data-testid="settings-whatsapp-pending-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <MessageCircle className="w-4 h-4" />
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div
          data-testid="settings-whatsapp-pending-empty"
          className="text-sm text-slate-400 italic py-6 text-center"
        >
          No open prompts — every cleaner picked a unit (or the menu expired).
        </div>
      ) : (
        <div className="space-y-2" data-testid="settings-whatsapp-pending-rows">
          {rows.map((p) => (
            <div
              key={p.id}
              data-testid={`settings-whatsapp-pending-${p.id}`}
              className="rounded-lg border border-amber-100 bg-amber-50/40 p-3 flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{p.company_name} · {p.phone}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Asked {new Date(p.asked_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                  {p.nudge_count ? ` · nudged ${p.nudge_count}×` : ""}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                  Options: {(p.options || []).map((o, i) => `${i+1}. ${o.name}`).join("  ·  ")}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {templates.length > 0 && (
                  <select
                    data-testid={`settings-whatsapp-pending-template-${p.id}`}
                    value={nudgeTemplate[p.id] || ""}
                    onChange={(e) => setNudgeTemplate({ ...nudgeTemplate, [p.id]: e.target.value })}
                    className="text-[10px] rounded-full border border-slate-200 bg-white text-slate-600 px-2 py-1"
                    title="Prepend a saved template · reply-rate within 2h shown"
                  >
                    <option value="">Default copy</option>
                    {templates.map((t) => {
                      const ins = insights[t.id];
                      const label = ins && ins.sends > 0
                        ? `${t.name} · ${ins.rate}% ≤2h (${ins.sends})`
                        : t.name;
                      return <option key={t.id} value={t.id}>{label}</option>;
                    })}
                  </select>
                )}
                <button
                  type="button"
                  disabled={busyId === p.id}
                  onClick={() => nudge(p)}
                  data-testid={`settings-whatsapp-pending-nudge-${p.id}`}
                  className="text-[10px] rounded-full bg-emerald-700 text-white px-2.5 py-1 hover:bg-emerald-800 transition disabled:opacity-40 inline-flex items-center gap-1"
                  title="Re-send the numbered menu via WhatsApp"
                >
                  <BellRing className="w-3 h-3" /> Nudge
                </button>
                <button
                  type="button"
                  disabled={busyId === p.id}
                  onClick={() => clear(p)}
                  data-testid={`settings-whatsapp-pending-clear-${p.id}`}
                  className="text-[10px] rounded-full border border-slate-200 bg-white text-slate-600 px-2.5 py-1 hover:bg-stone-50 transition disabled:opacity-40 inline-flex items-center gap-1"
                  title="Hard-resolve without picking a property"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {cronHealth && cronHealth.last_run_at && (
        <div
          data-testid="settings-whatsapp-pending-cron-health"
          className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-between gap-2 flex-wrap"
          title={`Hourly cleanup last ran at ${new Date(cronHealth.last_run_at).toLocaleString("en-GB")}`}
        >
          <span>
            Hourly cleanup last ran{" "}
            <span className="text-slate-600">
              {_relativeTime(cronHealth.last_run_at)}
            </span>
            {" "}·{" "}
            {cronHealth.last_expired_count === 0
              ? "nothing to expire"
              : `expired ${cronHealth.last_expired_count} prompt${cronHealth.last_expired_count === 1 ? "" : "s"}`}
          </span>
          <span className={`inline-flex items-center gap-1 ${_isStale(cronHealth.last_run_at) ? "text-amber-600" : "text-emerald-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${_isStale(cronHealth.last_run_at) ? "bg-amber-500" : "bg-emerald-500"}`} />
            {_isStale(cronHealth.last_run_at) ? "stale" : "alive"}
          </span>
        </div>
      )}
    </div>
  );
}

function _relativeTime(iso) {
  const dt = new Date(iso).getTime();
  const diffMs = Date.now() - dt;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} days ago`;
}

function _isStale(iso) {
  const dt = new Date(iso).getTime();
  // Cron runs hourly; consider stale after 90 min with a 30-min grace.
  return Date.now() - dt > 90 * 60 * 1000;
}
